class ProductItem extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      productMediaTemplate: "[js-quick-view-media-template]",
      quickShopButton: "[js-quick-shop-button]",
      swatchImages: "[js-product-item-swatch-image]",
      swatches: "[js-swatch]",
      swatchOverflowButton: "[js-product-listing-swatch-overflow]",
    };

    this.classes = {
      active: "active",
      swatchActive: "swatch-active",
      selectedSwatch: "swatch--selected",
    };
  }

  connectedCallback() {
    this.controller = new AbortController();
    // Quick shop button present if feature enabled which means swatch overflow link opens modal
    this.quickShopButton = this.querySelector(this.selectors.quickShopButton);

    this.swatchImages = this.querySelectorAll(this.selectors.swatchImages);
    this.swatches = this.querySelectorAll(this.selectors.swatches);
    this.swatchOverflowButton = this.querySelector(
      this.selectors.swatchOverflowButton,
    );

    // If we have swatches, add click listeners to each swatch
    this.swatches.forEach((swatch) => {
      swatch.addEventListener("click", this.handleSwatchClick.bind(this), {
        signal: this.controller.signal,
      });
    });

    // If we have swatch overflow, add a click listener
    if (this.swatchOverflowButton && this.quickShopButton) {
      this.swatchOverflowButton.addEventListener(
        "click",
        this.handleSwatchOverflowClick.bind(this),
        { signal: this.controller.signal },
      );
    }

    // Watch for Yotpo review count elements and extract just the number
    this.watchYotpoReviewCount();
    
    // Make Yotpo star ratings clickable to navigate to product page
    this.setupYotpoClickNavigation();
  }

  watchYotpoReviewCount() {
    const processReviewCount = (el) => {
      if (el && el.textContent && !el.dataset.processed) {
        const number = el.textContent.trim().split(' ')[0];
        el.textContent = number;
        el.dataset.processed = 'true';
      }
    };

    // Process any existing elements
    const existingElements = this.querySelectorAll('.product-item__details .yotpo-sr-bottom-line-right-panel');
    existingElements.forEach(processReviewCount);

    // Watch for new elements being added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node itself matches
            if (node.matches && node.matches('.product-item__details .yotpo-sr-bottom-line-right-panel')) {
              processReviewCount(node);
            }
            // Check if any children match
            const childElements = node.querySelectorAll && node.querySelectorAll('.product-item__details .yotpo-sr-bottom-line-right-panel');
            if (childElements) {
              childElements.forEach(processReviewCount);
            }
          }
        });
      });
    });

    // Start observing
    observer.observe(this, {
      childList: true,
      subtree: true
    });

    // Store observer for cleanup
    this.yotpoObserver = observer;
  }

  setupYotpoClickNavigation() {
    const makeClickable = (widget) => {
      if (widget && !widget.dataset.clickable) {
        widget.style.cursor = 'pointer';
        widget.dataset.clickable = 'true';
        
        widget.addEventListener('click', (e) => {
          // Get product URL from the product item link
          const productLink = this.querySelector('.product-item__image--main');
          if (productLink && productLink.href) {
            window.location.href = productLink.href;
          }
        }, { signal: this.controller.signal });
      }
    };

    // Process any existing widgets
    const existingWidgets = this.querySelectorAll('.product-item__details .yotpo-reviews-star-ratings-widget');
    existingWidgets.forEach(makeClickable);

    // Watch for new widgets being added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Check if the added node itself matches
            if (node.matches && node.matches('.product-item__details .yotpo-reviews-star-ratings-widget')) {
              makeClickable(node);
            }
            // Check if any children match
            const childWidgets = node.querySelectorAll && node.querySelectorAll('.product-item__details .yotpo-reviews-star-ratings-widget');
            if (childWidgets) {
              childWidgets.forEach(makeClickable);
            }
          }
        });
      });
    });

    // Start observing
    observer.observe(this, {
      childList: true,
      subtree: true
    });

    // Store observer for cleanup
    this.yotpoClickObserver = observer;
  }

  handleSwatchClick(event) {
    const swatch = event.currentTarget;

    // If the swatch is already selected, deselect it and return
    if (swatch.classList.contains(this.classes.selectedSwatch)) {
      swatch.classList.remove(this.classes.selectedSwatch);
      this.classList.remove(this.classes.swatchActive);

      for (let i = 0; i < this.swatchImages.length; i++) {
        const swatchImage = this.swatchImages[i];

        if (swatchImage.dataset.swatchValue === swatch.dataset.value) {
          swatchImage.classList.remove(this.classes.active);
          break
        }
      }

      return
    }

    // Hide currently active swatch image
    this.swatchImages.forEach((swatchImage) => {
      swatchImage.classList.remove(this.classes.active);
    });

    // Deselect all swatches
    this.swatches.forEach((swatch) => {
      swatch.classList.remove(this.classes.selectedSwatch);
    });

    // Select the current swatch and show the associated image
    swatch.classList.add(this.classes.selectedSwatch);

    let foundSwatchImage = false;

    for (let i = 0; i < this.swatchImages.length; i++) {
      const swatchImage = this.swatchImages[i];

      if (swatchImage.dataset.swatchValue === swatch.dataset.value) {
        this.classList.add(this.classes.swatchActive);
        swatchImage.classList.add(this.classes.active);
        foundSwatchImage = true;
        break
      }
    }

    if (!foundSwatchImage) {
      this.classList.remove(this.classes.swatchActive);
    }
  }

  handleSwatchOverflowClick() {
    this.openQuickView();
  }

  disconnectedCallback() {
    this.controller.abort();
    
    // Disconnect Yotpo observers
    if (this.yotpoObserver) {
      this.yotpoObserver.disconnect();
    }
    if (this.yotpoClickObserver) {
      this.yotpoClickObserver.disconnect();
    }
  }
}

if (!customElements.get("product-item")) {
  customElements.define("product-item", ProductItem);
}
